import { useContext } from 'react';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Menu from '@mui/material/Menu';
import Typography from '@mui/material/Typography';
import Avatar from '@mui/material/Avatar';
import SettingsIcon from '@mui/icons-material/Settings';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import LockResetIcon from '@mui/icons-material/LockReset';
import LogoutIcon from '@mui/icons-material/Logout';
import BugReportOutlinedIcon from '@mui/icons-material/BugReportOutlined';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import { useHistory } from 'react-router-dom';
import { Context as AuthContext } from '../../../api/auth/context';
import { useReportIssue } from '../../ReportIssue';

import type { User } from '../../../api/users/types';
import { useHeaderUserBoxStyles } from './styles/headerUserBox.styles';
import {
    MENU_ICON_BOX_SX,
    MENU_ICON_SX,
    MENU_ITEM_SX,
    MENU_LABEL_SX,
    SUBTLE_DIVIDER_SX
} from './styles/UserMenu.style';

interface CurrUserWithRole extends User {
    role_type?: string; // joined from roles table server-side, not part of the User type
}

interface UserMenuProps {
    anchorEl: HTMLElement | null;
    onClose: () => void;
    initials: string;
    onUploadClick: () => void;
    onOpenSettings: () => void;
    onOpenColorTheme: () => void;
}

export default function UserMenu({
    anchorEl,
    onClose,
    initials,
    onUploadClick,
    onOpenSettings,
    onOpenColorTheme
}: UserMenuProps) {
    const auth = useContext(AuthContext);
    const currUser = auth?.currUser as CurrUserWithRole | null;
    const { openReportIssue } = useReportIssue();
    const history = useHistory();

    const classes = useHeaderUserBoxStyles();
    const profilePic = currUser?.profilePicture;

    return (
        <Menu
            anchorEl={anchorEl}
            keepMounted
            open={Boolean(anchorEl)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            onClose={onClose}
            className={classes.menu}
            PaperProps={{
                elevation: 0,
                sx: ({ palette }) => {
                    const isDark = palette.mode === 'dark';
                    return {
                        mt: 0.75,
                        border: isDark ? `1px solid ${palette.divider}` : 'none',
                        borderRadius: '18px',
                        overflow: 'hidden',
                        minWidth: 260,
                        backgroundColor: palette.background.paper,
                        boxShadow: isDark
                            ? '0 10px 30px rgba(0,0,0,0.5), 0 2px 8px rgba(0,0,0,0.4)'
                            : '0 10px 30px rgba(15,23,42,0.10), 0 2px 8px rgba(15,23,42,0.06)'
                    };
                }
            }}
        >
            <div className={`${classes.menuRoot} dropdown-menu-right dropdown-menu-lg`}>
                <List className={classes.menuList} sx={{ minWidth: 260, py: 0 }}>
                    <Box
                        sx={(theme) => ({
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 1.5,
                            px: 2,
                            pt: 2.25,
                            pb: 1.5,
                            backgroundColor:
                                theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : '#fafbfd'
                        })}
                    >
                        <Avatar
                            className={classes.avatar}
                            sx={{ width: 36, height: 36, fontSize: '0.85rem' }}
                            src={profilePic || undefined}
                        >
                            {!profilePic && initials}
                        </Avatar>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                minWidth: 0,
                                flex: 1,
                                gap: 0.625
                            }}
                        >
                            <Typography
                                sx={{
                                    fontWeight: 600,
                                    fontSize: '0.95rem',
                                    letterSpacing: '-0.01em',
                                    lineHeight: 1.25,
                                    color: 'text.primary',
                                    textTransform: 'capitalize',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                }}
                            >
                                {currUser?.name}
                            </Typography>
                            {currUser?.role_type && (
                                <Box>
                                    <span className={classes.roleBadge}>{currUser.role_type}</span>
                                </Box>
                            )}
                            <Typography
                                sx={{
                                    fontSize: '0.75rem',
                                    fontWeight: 400,
                                    color: 'text.secondary',
                                    lineHeight: 1.2,
                                    mt: '2px',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                }}
                            >
                                {currUser?.email}
                            </Typography>
                        </Box>
                    </Box>
                    <Divider sx={SUBTLE_DIVIDER_SX} />
                    <Box sx={{ py: 0.5 }}>
                        <ListItem button onClick={onUploadClick} sx={MENU_ITEM_SX}>
                            <Box sx={MENU_ICON_BOX_SX}>
                                <PhotoCameraIcon sx={MENU_ICON_SX} />
                            </Box>
                            <Typography sx={MENU_LABEL_SX}>Upload Picture</Typography>
                        </ListItem>
                        <ListItem
                            button
                            onClick={() => {
                                onClose();
                                auth.openChangePassword?.();
                            }}
                            sx={MENU_ITEM_SX}
                        >
                            <Box sx={MENU_ICON_BOX_SX}>
                                <LockResetIcon sx={MENU_ICON_SX} />
                            </Box>
                            <Typography sx={MENU_LABEL_SX}>Change Password</Typography>
                        </ListItem>
                        <ListItem
                            button
                            onClick={() => {
                                onClose();
                                onOpenSettings();
                            }}
                            sx={MENU_ITEM_SX}
                        >
                            <Box sx={MENU_ICON_BOX_SX}>
                                <SettingsIcon sx={MENU_ICON_SX} />
                            </Box>
                            <Typography sx={MENU_LABEL_SX}>Table Settings</Typography>
                        </ListItem>
                        <ListItem
                            button
                            onClick={() => {
                                onClose();
                                onOpenColorTheme();
                            }}
                            sx={MENU_ITEM_SX}
                        >
                            <Box sx={MENU_ICON_BOX_SX}>
                                <PaletteOutlinedIcon sx={MENU_ICON_SX} />
                            </Box>
                            <Typography sx={MENU_LABEL_SX}>Colour Theme</Typography>
                        </ListItem>
                        <ListItem
                            button
                            onClick={() => {
                                onClose();
                                openReportIssue('bug');
                            }}
                            sx={MENU_ITEM_SX}
                        >
                            <Box sx={MENU_ICON_BOX_SX}>
                                <BugReportOutlinedIcon sx={MENU_ICON_SX} />
                            </Box>
                            <Typography sx={MENU_LABEL_SX}>Report an Issue</Typography>
                        </ListItem>
                        <ListItem
                            button
                            onClick={() => {
                                onClose();
                                history.push('/my-tickets');
                            }}
                            sx={MENU_ITEM_SX}
                        >
                            <Box sx={MENU_ICON_BOX_SX}>
                                <ConfirmationNumberOutlinedIcon sx={MENU_ICON_SX} />
                            </Box>
                            <Typography sx={MENU_LABEL_SX}>My Tickets</Typography>
                        </ListItem>
                    </Box>
                    <Divider sx={SUBTLE_DIVIDER_SX} />
                    <Box sx={{ py: 0.5 }}>
                        <ListItem button onClick={auth.logout} sx={MENU_ITEM_SX}>
                            <Box sx={MENU_ICON_BOX_SX}>
                                <LogoutIcon sx={MENU_ICON_SX} />
                            </Box>
                            <Typography sx={MENU_LABEL_SX}>Log out</Typography>
                        </ListItem>
                    </Box>
                </List>
            </div>
        </Menu>
    );
}
